<template>
  <div class="rental-list">
    <div class="content">
      <!-- 筛选头部 -->
      <div class="list-head">
        <h1 class="list-title">
          {{ isEn && communityName ? toPinyin(communityName) : (communityName || $t('rental.title')) }}
        </h1>

        <div class="publish-btn" v-if="isLandlord" @click="goPublish">
          {{ $t('rental.publish') }}
        </div>
      </div>

      <!-- 排序按钮 -->
      <div class="sort-tabs">
        <div class="sort-item" :class="{ active: currentSort === 'default' }" @click="onSortChange('default')">
          {{ $t('rental.sortDefault') }}
        </div>

        <div class="divider"></div>

        <div class="sort-item" :class="{ active: currentSort === 'price' }" @click="onSortChange('price')">
          {{ $t('rental.sortPrice') }}
        </div>

        <div class="divider"></div>

        <div class="sort-item" :class="{ active: currentSort === 'time' }" @click="onSortChange('time')">
          {{ $t('rental.sortTime') }}
        </div>
      </div>

      <!-- 房源列表 -->
      <van-list v-model="loading" :finished="finished" :finished-text="$t('common.noMore')" @load="loadData" :offset="100">
        <div class="rent-card" v-for="(item, index) in list" :key="item.id" @click="goInfo(item.id)">
          <div class="owner">
            <van-icon name="user-o" size="20" color="#1e5bff" />
            <span>{{ formatLandlord(item) }}</span>
          </div>

          <div class="phone-btn" @click.stop="callLandlord(item.landlordMobile)">
            <van-icon name="phone-o" />
            {{ $t('rental.callPhone') }}
          </div>

          <div class="house-name">
            {{ formatBuildingAddress(item.subareaName, item.buildingName, isEn) }}
          </div>

          <div class="house-desc">
            {{ isEn ? (item.descriptionEn || '') : (item.descriptionZh || '') }}
          </div>

          <div class="info-row" v-if="getAllTags(item).length">
            <span
              class="tag"
              v-for="(label, idx) in (expandedMap[index] || getAllTags(item).length <= 4) ? getAllTags(item) : getAllTags(item).slice(0, 4)"
              :key="idx"
            >{{ label }}</span>
            <van-icon v-if="getAllTags(item).length > 4" class="expand-btn" :name="expandedMap[index] ? 'arrow-up' : 'arrow-down'" size="22" @click.stop="toggleExpand(index)" />
          </div>

          <div class="time">
            <van-icon name="clock-o" />
            {{ item.createTime || $t('rental.newListing') }}
          </div>

          <div class="price">
            {{ formatPrice(item) }}
          </div>
        </div>
      </van-list>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { pinyin } from 'pinyin-pro'
import { getImageStream } from '@/utils/index'
import { toPinyin, formatBuildingAddress } from './addressUtil'

const compoundSurnames =[
  '欧阳', '司马', '上官', '诸葛', '东方', '独孤', '南宫', '夏侯',
  '尉迟', '皇甫', '慕容', '宇文', '令狐', '长孙', '公孙', '呼延',
  '司徒', '拓跋', '端木', '申屠', '万俟', '闻人', '赫连', '澹台',
  '公羊', '宗政', '濮阳', '太史', '闾丘', '轩辕', '巫马', '乐正',
  '公冶', '漆雕',
]

function getSurname(name) {
  for (const cs of compoundSurnames) {
    if (name.startsWith(cs)) {
      const py = pinyin(cs, { toneType: 'none', type: 'array' })
      return { pinyin: py.join(''), chinese: cs }
    }
  }
  const first = name.charAt(0)
  const py = pinyin(first, { toneType: 'none', type: 'array' })
  return { pinyin: py[0], chinese: first }
}


export default {
  data() {
    return {
      loading: true,
      finished: false,
      page: 1,
      limit: 10,
      currentSort: 'default',
      list: [],
      isLandlord: false,
      expandedMap: {},
      community: '',
      communityName: '',
      street: '',
      subarea: '',
      tagsDict: []
    }
  },
  created() {
    this.handleDomain()
    this.subarea = this.$route.query.subarea || ''
    this.loadTags()
    var qrParams = this.$route.query.params
    if (qrParams) {
      this.loadFromQrCode(qrParams)
    } else {
      this.community = this.$orgId || ''
      this.street = this.$globalData && this.$globalData.userInfo ? this.$globalData.userInfo.street || '' : ''
      this.loading = false
    }
  },
  computed: {
    isEn() {
      return Vue.config.lang === 'en-US'
    },
  },
  methods: {
    toPinyin,
    formatBuildingAddress,
    handleDomain() {
      var domain = this.$route.query.domain
      if (domain && domain !== 'null' && domain !== '0' && domain !== 'undefined') {
        if (domain === 'https://sgg.hzxh.gov.cn') {
          domain = 'https://shgz.hzxh.gov.cn'
          this.$route.query.domain = domain
        }
        Vue.prototype.$globalData.domain = decodeURIComponent(domain)
        this.$http.changeUrl(domain, true)
        this.$httpApp.changeUrl(domain, true)
        this.$httpCustApp.changeUrl(domain, true)
      }
    },
    loadFromQrCode(id) {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/scanCode/getScanCodeParams'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ id })
      }).then(({ data }) => {
        if (data && data.code === 0) {
          var orgType = data.data.orgType
          var orgId = data.data.orgId
          if (orgType === 'street') {
            this.street = orgId
          } else if (orgType === 'community') {
            this.community = orgId
          } else if (orgType === 'subarea') {
            this.subarea = orgId
          }
          this.loading = false
        }
      }).catch(() => {
        this.$toast.fail(this.$t('common.loadFail'))
      })
    },
    loadData() {
      if (this.finished) return

      this.$toast.loading({ duration: 0, message: this.$t('common.loading'), forbidClick: true })

      const isQrCode = !!this.$route.query.params
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl(isQrCode ? '/wxapp/landlord/listing/publicList' : '/wxapp/landlord/listing/list'),
        method: 'get',
        params: this.$httpCustApp.adornParams({
          page: this.page,
          limit: this.limit,
          sort: this.currentSort,
          community: this.community,
          street: this.street,
          subarea: this.subarea || null,
          // 仅英文环境要求已翻译(description_en 非空)的房源;中文环境不过滤,全部上架房源可见
          bilingual: this.isEn ? true : undefined,
          status: 1
        })
      }).then(({ data }) => {
        this.$toast.clear()
        this.loading = false

        if (data.code === 0) {
          const pageData = data.page || {}
          const list = pageData.list || []

          const records = list.map(item => {
            let images = []
            try {
              images = typeof item.images === 'string' ? JSON.parse(item.images) : (item.images || [])
            } catch (e) {
              images = []
            }
            return {
              ...item,
              address: item.address || '',
              landlordName: item.landlordName || '',
              imagePath: images.length ? getImageStream(images[0]) : ''
            }
          })

          this.list = this.page === 1 ? records : [...this.list, ...records]

          if (this.page === 1 && records.length) {
            this.communityName = records[0].communityName || ''
          }

          if (!list.length || list.length < this.limit) {
            this.finished = true
          }

          if (this.page === 1) {
            this.isLandlord = data.isLandlord !== undefined ? data.isLandlord : true
          }

          this.page++
        } else {
          this.finished = true
          this.$toast.fail({ duration: 1500, forbidClick: true, message: data.msg || this.$t('common.loadFail') })
        }
      }).catch(() => {
        this.$toast.clear()
        this.loading = false
        this.finished = true
      })
    },
    onSortChange(value) {
      this.currentSort = value
      this.page = 1
      this.list = []
      this.finished = false
      this.loading = true
      this.loadData()
    },
    formatLandlord(item) {
      const name = (item.landlordName || '').trim()
      if (!name) return ''
      const gender = item.landlordGender
      const surname = getSurname(name)
      const display = this.isEn
        ? surname.pinyin.charAt(0).toUpperCase() + surname.pinyin.slice(1)
        : surname.chinese
      if (gender === 1) return this.$t('rental.mr', { surname: display })
      if (gender === 2) return this.$t('rental.ms', { surname: display })
      return ''
    },
    loadTags() {
      this.$httpCustApp({
        url: this.$httpCustApp.adornUrl('/wxapp/sys/dict/listDictByCode'),
        method: 'get',
        params: this.$httpCustApp.adornParams({ code: 'landlord_tag' })
      }).then(({ data }) => {
        if (data.code === 0) {
          this.tagsDict = data.dicts || []
        }
      })
    },
    getTagLabels(item) {
      if (!item.tagIds) return []
      let ids = item.tagIds
      if (typeof ids === 'string') {
        try {
          ids = JSON.parse(ids)
        } catch (e) {
          ids = ids.split(',').map(id => id.trim())
        }
      }
      return ids
        .map(id => String(id))
        .map(id => this.tagsDict.find(d => String(d.value) === id))
        .filter(Boolean)
        .map(d => {
          if (this.isEn && d.extended) {
            try {
              const ext = JSON.parse(d.extended)
              if (ext.en) return ext.en
            } catch (e) { /* fall through */ }
          }
          return this.isEn ? 'Translating...' : d.label
        })
    },
    getAllTags(item) {
      const result = []
      if (this.isEn) {
        result.push(item.houseTypeNameEn || 'Translating...')
      } else if (item.houseTypeName) {
        result.push(item.houseTypeName)
      }
      const tags = this.isEn && item.tagsEn && item.tagsEn.length
        ? item.tagsEn
        : (this.isEn ? ['Translating...'] : item.tags)
      if (tags && tags.length) result.push(...tags)
      return result
    },
    toggleExpand(index) {
      this.$set(this.expandedMap, index, !this.expandedMap[index])
    },
    formatPrice(item) {
      if (item.minRent && item.maxRent) return this.$t('rental.priceRange', { min: item.minRent, max: item.maxRent })
      if (item.minRent) return this.$t('rental.priceFrom', { price: item.minRent })
      return item.maxRent || ''
    },
    goInfo(id) {
      this.$router.push({ path: '/saveCode-rental-info', query: { id } })
    },
    goPublish() {
      this.$router.push('/saveCode-rental-publish')
    },
    callLandlord(phone) {
      if (phone) {
        window.location.href = 'tel:' + phone
      }
    }
  }
}
</script>

<style scoped lang="scss">
.rental-list {
  min-height: 100vh;
  background: linear-gradient(180deg,
      #c3d2f9 0%,
      #eef4ff 48%,
      #ffffff 100%);


  .content {
    padding: 29px;
  }

  .list-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
  }

  .list-title {
    margin: 0;
    color: #1e5bff;
    font-size: 40px;
    font-weight: 800;
  }

  .publish-btn {
    width: 158px;
    height: 65px;

    border-radius: 22px;

    display: flex;
    justify-content: center;
    align-items: center;

    background: linear-gradient(116deg,
        #f9a01b 20%,
        #f5c33b 94%);

    color: #fff;
    font-size: 25px;
    font-weight: 800;
  }

  .sort-tabs {
    height: 90px;
    display: flex;
    align-items: center;

    background: #fff;
    border-radius: 29px;

    margin-bottom: 25px;
    overflow: hidden;
  }

  .sort-item {
    flex: 1;

    text-align: center;

    font-size: 27px;
    color: #0b1f3f;

    line-height: 90px;

    position: relative;

    &.active {
      color: #1e5bff;
      font-weight: 800;
    }

    &.active:after {
      content: '';

      position: absolute;

      left: 36px;
      right: 36px;
      bottom: 0;

      height: 4px;

      background: #1e5bff;
    }
  }

  .divider {
    width: 2px;
    height: 43px;
    background: #d9d9d9;
  }

  .rent-card {
    position: relative;

    height: 299px;

    background: #fff;
    border-radius: 29px;

    margin-bottom: 25px;

    padding: 25px 22px;

    box-shadow: 0 7px 14px rgba(0, 0, 0, .12);
  }

  .owner {
    display: flex;
    align-items: center;
    gap: 14px;

    color: #1e5bff;
    font-size: 25px;
  }

  .phone-btn {
    position: absolute;

    top: 27px;
    right: 29px;

    width: 180px;
    height: 54px;

    border: 2px solid #1e5bff;
    border-radius: 14px;

    display: flex;
    align-items: center;
    justify-content: center;

    gap: 7px;

    color: #1e5bff;
    background: #fff;

    font-size: 23px;
    font-weight: 700;
  }

  .house-name {
    margin-top: 28px;

    font-size: 36px;
    font-weight: 800;

    color: #0b1f3f;
  }

  .house-desc {
    margin-top: 18px;

    font-size: 25px;
    color: #555;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .info-row {
    margin-top: 14px;

    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;

    .expand-btn {
      color: #999;
      margin-left: 4px;
    }
  }

  .tag {
    height: 39px;
    line-height: 39px;

    padding: 0 14px;

    border-radius: 11px;

    background: #d9e4ff;

    color: #1e5bff;

    font-size: 22px;
    font-weight: 500;
    white-space: nowrap;
  }

  .time {
    position: absolute;

    left: 22px;
    bottom: 25px;

    display: flex;
    align-items: center;
    gap: 7px;

    color: #9b9b9b;

    font-size: 22px;
  }

  .price {
    position: absolute;

    right: 29px;
    bottom: 18px;

    color: #f7941e;

    font-size: 40px;
    font-weight: 800;
  }

}
</style>
